import React, { useRef } from 'react';
import { Smile } from 'lucide-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button/button';
import EmojiPicker from 'src/components/ui/emoji-picker';
import { yupResolver } from '@hookform/resolvers/yup';
import { CommentResponse } from 'src/lib/services/api/comment-api';
import { object, string } from 'yup';
import { useRequestCommentsContext } from 'src/features/comments/use-request-comments';
import { useToast } from 'src/components/ui/use-toast';
import {
  MentionTextarea,
  MentionTextareaHandle,
} from 'src/components/Form/mention-textarea';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';

type Props = {
  comment: CommentResponse;
  onCancel: () => void;
  onSuccess: () => void;
};

const validationSchema = object().shape({
  comment: string().required(),
});

const RevisionChatEditCommentForm: React.FC<Props> = ({ comment, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const mentionRef = useRef<MentionTextareaHandle | null>(null);
  // Requires a DecoratedRequestProvider higher in the tree. The Revisions
  // page wraps in one; any future mount site needs the same.
  const request = useDecoratedRequestContext();

  const { updateCommentMutation } = useRequestCommentsContext();

  const form = useForm<{ comment: string }>({
    defaultValues: { comment: comment.comment },
    /* eslint-disable */
    //@ts-ignore
    resolver: yupResolver(validationSchema),
  });

  const handleEmojiSelect = (emoji: any) => {
    // Delegate to MentionTextarea so the caret-to-markup translation stays
    // in one place. The edit form is the most likely place to hit the
    // markup-corruption bug since the comment being edited often already
    // contains `@[Name](uuid)` tokens.
    mentionRef.current?.insertAtCaret(emoji.native);
  };

  const submit = async (values: { comment: string }) => {
    updateCommentMutation.mutate([comment.id, { comment: values.comment }], {
      onSuccess: () => onSuccess(),
      onError: (e) => {
        console.error('Failed to update comment', e);
        toast({
          description: t('Oops, something went wrong, please try again later.'),
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submit)}>
        <Controller
          name={'comment'}
          control={form.control}
          render={({ field }) => (
            <MentionTextarea
              ref={mentionRef}
              requestId={request.id}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              rows={4}
              placeholder={t('Type a message...')!}
            />
          )}
        />
        <div className={'tw-mt-2 tw-flex tw-gap-2'}>
          <EmojiPicker onSelect={handleEmojiSelect}>
            <Button type={'button'} variant={'outline'} size={'sm'}>
              <Smile size={18} />
            </Button>
          </EmojiPicker>
          <div className={'tw-ml-auto tw-flex tw-gap-2'}>
            <Button onClick={onCancel} size={'sm'} variant={'outline'}>
              {t('Cancel')}
            </Button>
            <Button size={'sm'} variant={'brand'}>
              {t('Save')}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export { RevisionChatEditCommentForm };
